import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { formatItemStatValue, statUnitFor } from "@/helpers/statFormat"
import { generateUuid } from "@/helpers/uuid"
import {
  allocationErrorKey,
  applyDelta,
  buildSkillRows,
  draftToSkillAllocations,
  draftTotal,
  IAllocationDraft,
  remainingBank
} from "@/modals/constructor/hero/HeroModal/helpers"
import {
  ISkillEffectEntry,
  IStatOperation
} from "@/store/apis/catalogsAPI/catalogs.types"
import { useAllocateSkillPointsMutation } from "@/store/apis/heroAPI"
import styles from "./SkillList.module.scss"
import { ISkillListProps } from "./types"

const SKILL_EFFECT_OPERATION: IStatOperation = "FLAT"

export const SkillList: FC<ISkillListProps> = ({
  skills,
  catalog,
  stats,
  bank
}) => {
  const { t } = useTranslation("hero")
  const [allocate, { isLoading }] = useAllocateSkillPointsMutation()
  const [draft, setDraft] = useState<IAllocationDraft>({})
  const [errorKey, setErrorKey] = useState<string | null>(null)

  const rows = buildSkillRows(catalog, skills, draft)
  const staged = draftTotal(draft)
  const remaining = remainingBank(bank, draft)
  const isStale = remaining < 0

  const isSkillCatalogMissing = rows.length === 0

  if (isSkillCatalogMissing) {
    return null
  }

  const step = (code: string, delta: number, ceiling: number) => {
    setErrorKey(null)
    setDraft((current) =>
      applyDelta(current, code, delta, bank, { enabled: true, ceiling })
    )
  }

  const reset = () => {
    setErrorKey(null)
    setDraft({})
  }

  const confirm = async () => {
    setErrorKey(null)
    const freshIdempotencyKey = generateUuid()
    try {
      await allocate({
        requestId: freshIdempotencyKey,
        allocations: draftToSkillAllocations(draft)
      }).unwrap()
      setDraft({})
    } catch (error) {
      setErrorKey(allocationErrorKey(error, "skills"))
    }
  }

  const translatedEffectText = (effects: ISkillEffectEntry[]) =>
    effects
      .map((effect) => {
        const { text, note } = formatItemStatValue(
          effect.valuePerRank,
          statUnitFor(stats, effect.statCode),
          SKILL_EFFECT_OPERATION
        )
        const suffix = note === "points" ? ` ${t("unit.points")}` : ""
        const name = t(`catalog:stat.${effect.statCode}`, {
          defaultValue: effect.statCode
        })
        return `${text}${suffix} ${name}`
      })
      .join(", ")

  return (
    <section className={styles.SkillList}>
      <h3 className={styles.SkillList_title}>{t("skills.title")}</h3>
      <div className={styles.SkillList_bank}>
        <span>{t("banks.skillPoints")}</span>
        <span>
          {bank}
          {staged > 0 && ` → ${bank - staged}`}
        </span>
      </div>
      <ul className={styles.SkillList_rows}>
        {rows.map((row) => (
          <li key={row.code} className={styles.SkillList_row}>
            <div className={styles.SkillList_head}>
              <span>
                {t(`catalog:skill.${row.code}`, { defaultValue: row.code })}
              </span>
              <span className={styles.SkillList_rank}>
                {row.rank}
                {row.staged > 0 && (
                  <span className={styles.SkillList_staged}>
                    {" "}
                    +{row.staged}
                  </span>
                )}{" "}
                / {row.maxRank}
              </span>
              {row.rank >= row.maxRank ? (
                <span className={styles.SkillList_max}>{t("skills.max")}</span>
              ) : (
                <span className={styles.SkillList_controls}>
                  <button
                    type="button"
                    aria-label={t("allocate.decrease", {
                      name: t(`catalog:skill.${row.code}`, {
                        defaultValue: row.code
                      })
                    })}
                    disabled={isLoading || row.staged === 0}
                    onClick={() => step(row.code, -1, row.maxRank - row.rank)}
                  >
                    −
                  </button>
                  <button
                    type="button"
                    aria-label={t("allocate.increase", {
                      name: t(`catalog:skill.${row.code}`, {
                        defaultValue: row.code
                      })
                    })}
                    disabled={
                      isLoading ||
                      remaining < 1 ||
                      row.rank + row.staged >= row.maxRank
                    }
                    onClick={() => step(row.code, 1, row.maxRank - row.rank)}
                  >
                    +
                  </button>
                </span>
              )}
            </div>
            {row.effects.length > 0 && (
              <span className={styles.SkillList_effect}>
                {t("skills.perRank", {
                  effects: translatedEffectText(row.effects)
                })}
              </span>
            )}
          </li>
        ))}
      </ul>
      <div className={styles.SkillList_actions}>
        <button
          type="button"
          disabled={isLoading || staged === 0}
          onClick={reset}
        >
          {t("allocate.reset")}
        </button>
        <button
          type="button"
          disabled={isLoading || staged === 0 || isStale}
          onClick={() => void confirm()}
        >
          {t("allocate.confirm")}
        </button>
      </div>
      {isStale && (
        <span className={styles.SkillList_error}>
          {t("skills.error.staleBank")}
        </span>
      )}
      {errorKey && (
        <span className={styles.SkillList_error}>{t(errorKey)}</span>
      )}
    </section>
  )
}
